import Geometry from '@arcgis/core/geometry/Geometry';
import Point from '@arcgis/core/geometry/Point';
import Polygon from '@arcgis/core/geometry/Polygon';
import Graphic from '@arcgis/core/Graphic';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import SimpleFillSymbol from '@arcgis/core/symbols/SimpleFillSymbol';
import SimpleMarkerSymbol from '@arcgis/core/symbols/SimpleMarkerSymbol';
import MapView from '@arcgis/core/views/MapView';
import WebMap from '@arcgis/core/WebMap';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { MapContext } from './MapContextContainer';

function useUseDebug(webmap: WebMap, view: MapView) {
    const [isDebugOn, setIsDebugOn] = useState(false);
    const [searchParams] = useSearchParams();
    const mapContext = useContext(MapContext);

    const debugLayer = useMemo(() =>
        isDebugOn ? new GraphicsLayer({ title: "Debug" }) : null,
        [isDebugOn]);
    const debugLayerRectangles = useMemo(() =>
        isDebugOn ? new GraphicsLayer({ title: "Debug rectangles" }) : null,
        [isDebugOn]);
    const debugLayerSquares = useMemo(() =>
        isDebugOn ? new GraphicsLayer({ title: "Debug squares" }) : null,
        [isDebugOn]);
    const debugLayerPoints = useMemo(() =>
        isDebugOn ? new GraphicsLayer({ title: "Debug points" }) : null,
        [isDebugOn]);
    const debugLayerCoveredAreaLevel7 = useMemo(() =>
        isDebugOn ? new GraphicsLayer({ title: "Debug covered area level 7", visible: false, minScale: 76000, maxScale: 60000 }) : null,
        [isDebugOn]);
    const debugLayerCoveredAreaLevel6 = useMemo(() =>
        isDebugOn ? new GraphicsLayer({ title: "Debug covered area level 6", visible: false, minScale: 30506, maxScale: 30200 }) : null,
        [isDebugOn]);
    const debugLayerCoveredAreaLevel5 = useMemo(() =>
        isDebugOn ? new GraphicsLayer({ title: "Debug covered area level 5", visible: false, minScale: 17000, maxScale: 15100 }) : null,
        [isDebugOn]);
    const debugLayerCoveredAreaLevel4 = useMemo(() =>
        isDebugOn ? new GraphicsLayer({ title: "Debug covered area level 4", visible: false, minScale: 7659, maxScale: 7459 }) : null,
        [isDebugOn]);
    const debugLayerCoveredAreaLevel3 = useMemo(() =>
        isDebugOn ? new GraphicsLayer({ title: "Debug covered area level 3", visible: false, minScale: 3800, maxScale: 3700 }) : null,
        [isDebugOn]);
    const debugLayerCoveredAreaLevel2 = useMemo(() =>
        isDebugOn ? new GraphicsLayer({ title: "Debug covered area level 2", visible: false, minScale: 1900, maxScale: 1800 }) : null,
        [isDebugOn]);

    const debugLayerCoveredAreaLevel1 = useMemo(() =>
        isDebugOn ? new GraphicsLayer({ title: "Debug covered area level 1", visible: false, minScale: 1000, maxScale: 900 }) : null,
        [isDebugOn]);

    useEffect(() => {
        if (searchParams.get("debug") === "true") {
            setIsDebugOn(true);
        }
    }, [searchParams]);

    useEffect(() => {
        if (isDebugOn &&
            debugLayer &&
            debugLayerSquares &&
            debugLayerPoints &&
            debugLayerCoveredAreaLevel2 &&
            debugLayerCoveredAreaLevel1 &&
            debugLayerCoveredAreaLevel3 &&
            debugLayerCoveredAreaLevel4 &&
            debugLayerCoveredAreaLevel5 &&
            debugLayerCoveredAreaLevel6 &&
            debugLayerCoveredAreaLevel7 &&
            debugLayerRectangles) {

            webmap.add(debugLayer);
            webmap.add(debugLayerSquares);
            webmap.add(debugLayerPoints);
            webmap.add(debugLayerCoveredAreaLevel1);
            webmap.add(debugLayerCoveredAreaLevel2);
            webmap.add(debugLayerCoveredAreaLevel3);
            webmap.add(debugLayerCoveredAreaLevel4);
            webmap.add(debugLayerCoveredAreaLevel5);
            webmap.add(debugLayerCoveredAreaLevel6);
            webmap.add(debugLayerCoveredAreaLevel7);
            webmap.add(debugLayerRectangles);
        }
    }, [mapContext.mapAndViewReady, isDebugOn]);

    const debugCoveredArea = (coveredArea: Geometry) => {
        if (isDebugOn) {
            const rightDebugLayer = getDebugLayerForCoveredArea();

            rightDebugLayer.add(new Graphic({
                symbol: new SimpleFillSymbol({
                    color: "lightgreen",
                    outline: {
                        color: "red",
                        width: 2
                    }
                }),
                geometry: coveredArea
            }));
        }
    }

    const debugSquare = (square: Polygon) => {
        if (!isDebugOn || debugLayerSquares === null) return;

        debugLayerSquares.add(new Graphic({
            symbol: new SimpleFillSymbol({
                color: "lightgrey",
                outline: {
                    color: "red",
                    width: 2
                }
            }),
            geometry: square
        }));
    }

    const debugPoint = (point: Point) => {
        if (!isDebugOn || debugLayerPoints === null) return;

        debugLayerPoints.add(new Graphic({
            symbol: new SimpleMarkerSymbol({
                color: "white",
                size: 50
            }),
            geometry: point
        }));
    }

    const debugRectangles = (rectangles: Geometry[]) => {
        if (!isDebugOn || debugLayerRectangles === null) return;

        rectangles.forEach(element => {
            debugLayerRectangles.add(new Graphic({
                symbol: new SimpleFillSymbol({
                    // color: "black",
                    outline: {
                        color: "red",
                        width: 4
                    }
                }),
                geometry: element
            }));
        });
    }

    function getDebugLayerForCoveredArea(): GraphicsLayer {
        if (view.zoom === 9 && debugLayerCoveredAreaLevel7) {
            return debugLayerCoveredAreaLevel7;
        }
        else if (view.zoom === 10 && debugLayerCoveredAreaLevel6) {
            return debugLayerCoveredAreaLevel6;
        }
        else if (view.zoom === 11 && debugLayerCoveredAreaLevel5) {
            return debugLayerCoveredAreaLevel5;
        }
        else if (view.zoom === 12 && debugLayerCoveredAreaLevel4) {
            return debugLayerCoveredAreaLevel4;
        }
        else if (view.zoom === 13 && debugLayerCoveredAreaLevel3) {
            return debugLayerCoveredAreaLevel3;
        }
        else if (view.zoom === 14 && debugLayerCoveredAreaLevel2) {
            return debugLayerCoveredAreaLevel2;
        }
        else if (view.zoom === 15 && debugLayerCoveredAreaLevel1) {
            return debugLayerCoveredAreaLevel1;
        }
        else {
            throw "Not found debug layer for zoom " + view.zoom + " at scale " + view.scale;
        }
    }

    return {
        isDebugOn,
        debugCoveredArea,
        debugSquare,
        debugPoint,
        debugRectangles
    };
};

export default useUseDebug;